import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import AddCardModal from "@/components/AddCardModal";
import { toast } from "sonner";
import { Bell, CreditCard, Trash2, DollarSign, AlertTriangle, Save, Fingerprint, Shield, Smartphone, Loader2, Plus, Check } from "lucide-react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useTheme } from "@/contexts/ThemeContext";
import "@/styles/commandDeck.css";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const NAV_SECTIONS = [
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'payout', label: 'Payout Details', icon: DollarSign },
  { id: 'payment', label: 'Payment Methods', icon: CreditCard },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'account', label: 'Account', icon: AlertTriangle },
];

const Settings = ({ onLogout }) => {
  const { isDark } = useTheme();
  const initialUser = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
  const [userData, setUserData] = useState(initialUser);
  const [activeSection, setActiveSection] = useState('notifications');
  const sectionRefs = useRef({});

  // Email Notifications State
  const [notifications, setNotifications] = useState({
    newJobs: initialUser.email_notifications ?? true,
    newLeads: initialUser.notifications?.newLeads ?? true,
    newsletter: initialUser.notifications?.newsletter ?? false,
  });

  // Job Notification Preferences - Convert from database format
  const [jobNotificationRoles, setJobNotificationRoles] = useState([]);
  const [notificationRadius, setNotificationRadius] = useState(initialUser.job_location_radius || initialUser.notification_radius || 200);

  // Fetch latest user data from API on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (initialUser.id) {
        try {
          const token = localStorage.getItem('shootzu_token');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/${initialUser.id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          const data = response.data;
          setUserData(prev => ({ ...prev, ...data }));

          // Update banking details if present
          if (data.banking_details) {
            setBankingDetails({
              bankName: data.banking_details.bank_name || '',
              accountName: data.banking_details.bank_account_name || data.banking_details.account_name || '',
              bsb: data.banking_details.bsb || '',
              accountNumber: data.banking_details.account_number || '',
            });
          }

          // Update notification preferences if present
          if (data.notification_preferences) {
            const prefs = data.notification_preferences;
            setNotifications(prev => ({
              ...prev,
              newJobs: prefs.job_alerts ?? prefs.email_notifications ?? prev.newJobs,
              newLeads: prefs.new_lead ?? prev.newLeads,
            }));
          }

          // Update notification radius if present
          if (data.job_location_radius) {
            setNotificationRadius(data.job_location_radius);
          }
        } catch (error) {
          console.log('Could not fetch latest user data:', error);
        }
      }
    };
    fetchUserData();
  }, [initialUser.id]);

  // Load notification preferences from userData whenever it updates
  useEffect(() => {
    const prefs = userData.notification_preferences?.job_types || {};
    const roles = [];
    if (prefs.PHOTOGRAPHER) roles.push('photographer');
    if (prefs.VIDEOGRAPHER) roles.push('videographer');
    if (prefs.PHOTO_EDITOR) roles.push('editor_photos');
    if (prefs.VIDEO_EDITOR) roles.push('editor_video');
    if (prefs.ASSISTANT) roles.push('assistant');
    if (prefs.DRONE_OPERATOR) roles.push('drone_operator');
    setJobNotificationRoles(roles);

    if (userData.job_location_radius) {
      setNotificationRadius(userData.job_location_radius);
    }
  }, [userData]);

  // Track active section on scroll via IntersectionObserver
  useEffect(() => {
    const observers = [];
    const options = { rootMargin: '-20% 0px -60% 0px', threshold: 0 };

    NAV_SECTIONS.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (!el) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(id);
        });
      }, options);
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const roleOptions = [
    { value: "videographer", label: "Videographer" },
    { value: "photographer", label: "Photographer" },
    { value: "editor_photos", label: "Editor (Photos)" },
    { value: "editor_video", label: "Editor (Video)" },
    { value: "assistant", label: "Assistant" },
    { value: "drone_operator", label: "Drone Operator" },
    { value: "voxpop_videographer", label: "VoxPop Videographer" }
  ];

  // Banking Details State
  const [bankingDetails, setBankingDetails] = useState({
    bankName: initialUser.bankDetails?.bankName || initialUser.banking_details?.bank_name || '',
    accountName: initialUser.bankDetails?.accountName || initialUser.banking_details?.bank_account_name || '',
    bsb: initialUser.bankDetails?.bsb || initialUser.banking_details?.bsb || '',
    accountNumber: initialUser.bankDetails?.accountNumber || initialUser.banking_details?.account_number || '',
  });

  // Credit Card State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Passkey State
  const [passkeys, setPasskeys] = useState([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(true);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const [passkeySupported] = useState(browserSupportsWebAuthn());

  // Load saved payment methods, banking details, and passkeys on mount
  useEffect(() => {
    loadPaymentMethods();
    loadBankingDetails();
    if (passkeySupported) {
      loadPasskeys();
    }
  }, []);

  const loadBankingDetails = async () => {
    try {
      const token = localStorage.getItem('shootzu_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/${userData.id}`,
        { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
      );

      // Update localStorage and state with fresh user data
      const updatedUser = { ...userData, ...response.data };
      localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
      setUserData(updatedUser);

      // Check for banking_details in response
      if (response.data.banking_details) {
        setBankingDetails({
          bankName: response.data.banking_details.bank_name || '',
          accountName: response.data.banking_details.bank_account_name || response.data.banking_details.account_name || '',
          bsb: response.data.banking_details.bsb || '',
          accountNumber: response.data.banking_details.account_number || ''
        });
      }
    } catch (error) {
      console.error('Failed to load banking details:', error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      setLoadingCards(true);
      const userEmail = userData.email || 'test@shootzu.com';
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods?user_email=${userEmail}`
      );
      setPaymentMethods(response.data.methods || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingCards(false);
    }
  };

  // Passkey Functions
  const loadPasskeys = async () => {
    try {
      setLoadingPasskeys(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/passkey/credentials?user_email=${userData.email}`
      );
      setPasskeys(response.data.credentials || []);
    } catch (error) {
      console.error('Failed to load passkeys:', error);
      setPasskeys([]);
    } finally {
      setLoadingPasskeys(false);
    }
  };

  const handleRegisterPasskey = async () => {
    if (!passkeySupported) {
      toast.error('Passkeys are not supported on this device.');
      return;
    }

    setRegisteringPasskey(true);
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to register passkeys');
      setRegisteringPasskey(false);
      return;
    }

    try {
      const optionsResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/passkey/register/options`,
        { user_email: userData.email },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const options = optionsResponse.data;

      const credential = await startRegistration({ optionsJSON: options });
      const deviceName = getDeviceName();

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/passkey/register/verify`,
        {
          user_email: userData.email,
          credential: credential,
          device_name: deviceName
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      toast.success('Passkey registered successfully!');
      loadPasskeys();
    } catch (error) {
      console.error('Passkey registration error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Registration was cancelled.');
      } else if (error.name === 'InvalidStateError') {
        toast.error('This passkey is already registered.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to register passkey.');
      }
    } finally {
      setRegisteringPasskey(false);
    }
  };

  const handleDeletePasskey = async (credentialId) => {
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to manage passkeys');
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/passkey/credentials/${credentialId}?user_email=${userData.email}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Passkey removed successfully');
      loadPasskeys();
    } catch (error) {
      console.error('Failed to delete passkey:', error);
      toast.error('Failed to remove passkey');
    }
  };

  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Android/.test(ua)) return 'Android Device';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Linux/.test(ua)) return 'Linux PC';
    return 'Unknown Device';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddPaymentMethod = () => {
    setShowAddCardModal(true);
  };

  const handleCardAdded = () => {
    loadPaymentMethods();
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to manage payment methods');
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods/${paymentMethodId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success("Payment method removed");
      loadPaymentMethods();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to remove payment method';
      toast.error(errorMessage);
      console.error('Remove error:', error);
    }
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to manage payment methods');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/methods/${paymentMethodId}/default`,
        { userEmail: userData.email || 'test@shootzu.com' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success("Default payment method updated");
      loadPaymentMethods();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to set default';
      toast.error(errorMessage);
      console.error('Set default error:', error);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRoleToggle = (roleValue) => {
    setJobNotificationRoles(prev => {
      if (prev.includes(roleValue)) {
        return prev.filter(r => r !== roleValue);
      } else {
        return [...prev, roleValue];
      }
    });
  };

  const handleRadiusChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setNotificationRadius(Math.max(0, Math.min(500, value)));
  };

  const handleBankingChange = (field, value) => {
    setBankingDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveNotifications = async () => {
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to save preferences');
      return;
    }

    try {
      const job_types = {
        PHOTOGRAPHER: jobNotificationRoles.includes('photographer'),
        VIDEOGRAPHER: jobNotificationRoles.includes('videographer'),
        PHOTO_EDITOR: jobNotificationRoles.includes('editor_photos'),
        VIDEO_EDITOR: jobNotificationRoles.includes('editor_video'),
        ASSISTANT: jobNotificationRoles.includes('assistant'),
        DRONE_OPERATOR: jobNotificationRoles.includes('drone_operator')
      };

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userData.id}`,
        {
          notification_preferences: {
            email_notifications: notifications.newJobs,
            sms_notifications: notifications.sms || false,
            job_alerts: notifications.newJobs,
            new_lead: notifications.newLeads,
            job_types: job_types
          },
          job_location_radius: notificationRadius
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const updatedUser = {
        ...userData,
        notifications,
        notification_preferences: {
          ...userData.notification_preferences,
          email_notifications: notifications.newJobs,
          job_alerts: notifications.newJobs,
          new_lead: notifications.newLeads,
          job_types: job_types
        },
        job_location_radius: notificationRadius
      };
      localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));

      toast.success("Notification preferences updated!");
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error("Failed to save preferences");
    }
  };

  const handleSaveBanking = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('shootzu_token');
    if (!token) {
      toast.error('Please log in to save banking details');
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userData.id}`, {
        banking_details: {
          bank_name: bankingDetails.bankName,
          bank_account_name: bankingDetails.accountName,
          bsb: bankingDetails.bsb,
          account_number: bankingDetails.accountNumber
        }
      }, { headers: { 'Authorization': `Bearer ${token}` } });

      const updatedUser = {
        ...userData,
        bankDetails: bankingDetails,
        banking_details: {
          bank_name: bankingDetails.bankName,
          bank_account_name: bankingDetails.accountName,
          bsb: bankingDetails.bsb,
          account_number: bankingDetails.accountNumber
        }
      };
      localStorage.setItem('shootzu_user', JSON.stringify(updatedUser));
      setUserData(updatedUser);

      toast.success("Banking details saved successfully!");
    } catch (error) {
      console.error('Error saving banking details:', error);
      toast.error("Failed to save banking details");
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const userDataString = localStorage.getItem('shootzu_user');
      const token = localStorage.getItem('shootzu_token');

      if (!userDataString || !token) {
        toast.error('Please log in to delete your account');
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData.id;

      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/account/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      localStorage.clear();
      toast.success("Account deleted successfully. You can sign up again anytime.");

      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again or contact support.');
    }
  };

  // Helper function for card brand display (Command Deck dark theme)
  const getCardBrandDisplay = (brand) => {
    const b = (brand || '').toLowerCase();
    const badgeStyle = {
      width: 44, height: 30, borderRadius: 7,
      background: 'linear-gradient(135deg,#572B7C,#28C0D0)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontFamily: 'var(--sz-font-display)', fontWeight: 700,
      fontSize: 10, flex: '0 0 auto', letterSpacing: '.5px',
    };
    if (b === 'visa') return <span style={badgeStyle}>VISA</span>;
    if (b === 'mastercard') return <span style={{ ...badgeStyle, fontSize: 8 }}>MC</span>;
    if (b === 'amex' || b === 'american express') return <span style={{ ...badgeStyle, fontSize: 9 }}>AMEX</span>;
    return <span style={badgeStyle}><CreditCard className="w-4 h-4" /></span>;
  };

  // ---- Command Deck shared style tokens ----
  const cardStyle = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px', scrollMarginTop: 90 };
  const cardTitleStyle = { fontFamily: 'var(--sz-font-display)', fontWeight: 700, fontSize: 16, color: '#fff' };
  const fieldLabelStyle = { fontSize: 11.5, color: 'rgba(255,255,255,.5)', marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '11px 13px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 11, background: 'rgba(255,255,255,.04)', color: '#fff', fontFamily: 'var(--sz-font-body)', fontSize: 13.5, outline: 'none' };
  const monoInputStyle = { ...inputStyle, fontFamily: 'var(--sz-font-mono)' };
  const addBtnStyle = { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 10, background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };
  const removeLinkStyle = { fontSize: 12, color: 'rgba(255,255,255,.5)', background: 'none', border: 'none', cursor: 'pointer' };
  const dialogStyle = { background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, color: '#fff' };

  const renderToggle = (checked, onClick, testid) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={checked}
      style={{ width: 44, height: 26, borderRadius: 999, background: checked ? '#28C0D0' : 'rgba(255,255,255,.14)', position: 'relative', flex: '0 0 auto', cursor: 'pointer', border: 'none', padding: 0 }}
    >
      <span style={{ position: 'absolute', top: 3, [checked ? 'right' : 'left']: 3, width: 20, height: 20, borderRadius: 999, background: checked ? '#0C1120' : 'rgba(255,255,255,.7)' }} />
    </button>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <div className="cd-root sidebar-content pt-16 lg:pt-20">
        <div className="cd-bloom-wrap">
          <div className="cd-bloom cd-bloom-1" />
          <div className="cd-bloom cd-bloom-2" />
        </div>

        <div style={{ position: 'relative', zIndex: 1, padding: '30px 40px 48px', maxWidth: 1080, margin: '0 auto' }}>
          {/* Page header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 700, fontSize: 11, letterSpacing: 2, color: '#28C0D0' }}>PREFERENCES</div>
            <div style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 800, fontSize: 32, letterSpacing: '-1px', color: '#fff', marginTop: 8 }}>Settings</div>
            {userData.email && (
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.5)', marginTop: 5 }}>Signed in as {userData.email}</div>
            )}
          </div>

          <div className="sz-settings" style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 26, alignItems: 'start' }}>
            {/* Section nav */}
            <div className="sz-settings-nav" style={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {NAV_SECTIONS.map(({ id, label, icon: Icon }) => {
                const active = activeSection === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => scrollToSection(id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px',
                      borderRadius: 11, fontSize: 13, fontWeight: active ? 600 : 500,
                      color: active ? '#fff' : 'rgba(255,255,255,.6)',
                      background: active ? 'rgba(40,192,208,.12)' : 'transparent',
                      border: active ? '1px solid rgba(40,192,208,.28)' : '1px solid transparent',
                      textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <Icon className="w-[17px] h-[17px]" />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* Notifications */}
              <div ref={el => sectionRefs.current.notifications = el} id="notifications" style={cardStyle}>
                <div style={{ ...cardTitleStyle, marginBottom: 18 }}>Notifications</div>

                {/* New job notifications */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>New job notifications</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>Get pinged when a matching gig is posted nearby.</div>
                  </div>
                  {renderToggle(notifications.newJobs, () => handleNotificationToggle('newJobs'), 'toggle-new-jobs')}
                </div>

                {/* Job type preferences + radius (only when new jobs on) */}
                {notifications.newJobs && (
                  <>
                    <div style={{ padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', marginBottom: 10 }}>Job type preferences</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {roleOptions.map((role) => {
                          const sel = jobNotificationRoles.includes(role.value);
                          return (
                            <button
                              key={role.value}
                              type="button"
                              onClick={() => handleRoleToggle(role.value)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                                color: sel ? '#0C1120' : 'rgba(255,255,255,.7)',
                                background: sel ? '#28C0D0' : 'rgba(255,255,255,.06)',
                                border: sel ? '1px solid transparent' : '1px solid rgba(255,255,255,.1)',
                                padding: '7px 13px', borderRadius: 999, cursor: 'pointer',
                              }}
                            >
                              {sel && <Check className="w-3 h-3" strokeWidth={3} />}
                              {role.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>Job location radius</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#28C0D0', fontFamily: 'var(--sz-font-display)' }}>
                          {notificationRadius} km <span style={{ fontWeight: 500, color: 'rgba(255,255,255,.45)' }}>from {userData.address || userData.location || 'your location'}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <input
                          type="number"
                          min="0"
                          max="500"
                          value={notificationRadius}
                          onChange={handleRadiusChange}
                          style={{ width: 92, padding: '9px 11px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, background: 'rgba(255,255,255,.04)', color: '#fff', fontFamily: 'var(--sz-font-mono)', fontSize: 13, outline: 'none' }}
                        />
                        <input
                          type="range"
                          min="0"
                          max="500"
                          step="10"
                          value={notificationRadius}
                          onChange={(e) => setNotificationRadius(parseInt(e.target.value))}
                          style={{ flex: 1, accentColor: '#28C0D0' }}
                        />
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,.5)' }}>km</span>
                      </div>
                    </div>
                  </>
                )}

                {/* New lead notifications */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>New lead notifications</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>Alerts when a fresh client lead lands in your area.</div>
                  </div>
                  {renderToggle(notifications.newLeads, () => handleNotificationToggle('newLeads'), 'toggle-new-leads')}
                </div>

                <button
                  type="button"
                  onClick={handleSaveNotifications}
                  data-testid="save-notifications-button"
                  style={{ marginTop: 14, padding: '11px 20px', borderRadius: 11, background: '#28C0D0', border: 'none', color: '#0C1120', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <Save className="w-4 h-4" />
                  Save preferences
                </button>
              </div>

              {/* Payout details */}
              <div ref={el => sectionRefs.current.payout = el} id="payout" style={cardStyle}>
                <div style={{ ...cardTitleStyle, marginBottom: 6 }}>Payout details</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', marginBottom: 18 }}>Where we send your earnings. Released on time, every job.</div>
                <form onSubmit={handleSaveBanking}>
                  <div className="sz-grid-2c" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <div style={fieldLabelStyle}>Name of bank <span style={{ color: '#F87171' }}>*</span></div>
                      <input
                        id="bankName"
                        data-testid="bank-name-input"
                        value={bankingDetails.bankName}
                        onChange={(e) => handleBankingChange('bankName', e.target.value)}
                        placeholder="e.g., Commonwealth Bank"
                        required
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <div style={fieldLabelStyle}>Name on account <span style={{ color: '#F87171' }}>*</span></div>
                      <input
                        id="accountName"
                        data-testid="account-name-input"
                        value={bankingDetails.accountName}
                        onChange={(e) => handleBankingChange('accountName', e.target.value)}
                        placeholder="John Doe"
                        required
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <div style={fieldLabelStyle}>BSB <span style={{ color: '#F87171' }}>*</span></div>
                      <input
                        id="bsb"
                        data-testid="bsb-input"
                        value={bankingDetails.bsb}
                        onChange={(e) => handleBankingChange('bsb', e.target.value)}
                        placeholder="000-000"
                        maxLength={7}
                        required
                        style={monoInputStyle}
                      />
                    </div>
                    <div>
                      <div style={fieldLabelStyle}>Account number <span style={{ color: '#F87171' }}>*</span></div>
                      <input
                        id="accountNumber"
                        data-testid="account-number-input"
                        value={bankingDetails.accountNumber}
                        onChange={(e) => handleBankingChange('accountNumber', e.target.value)}
                        placeholder="12345678"
                        required
                        style={monoInputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(40,192,208,.06)', border: '1px solid rgba(40,192,208,.18)' }}>
                    <Shield className="w-4 h-4 shrink-0" style={{ marginTop: 1, color: '#28C0D0' }} />
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', margin: 0 }}>
                      Your banking details are encrypted and securely stored. They will be used to process payments when you complete jobs.
                    </p>
                  </div>

                  <button
                    type="submit"
                    data-testid="save-banking-button"
                    style={{ marginTop: 16, padding: '11px 20px', borderRadius: 11, background: '#15803D', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    <Save className="w-4 h-4" />
                    Save banking details
                  </button>
                </form>
              </div>

              {/* Payment methods */}
              <div ref={el => sectionRefs.current.payment = el} id="payment" style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={cardTitleStyle}>Payment methods</div>
                  {!loadingCards && paymentMethods.length > 0 && (
                    <button type="button" onClick={handleAddPaymentMethod} data-testid="add-payment-button" style={addBtnStyle}>
                      <Plus className="w-[15px] h-[15px]" strokeWidth={2.4} />
                      Add card
                    </button>
                  )}
                </div>

                {loadingCards ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '34px 0' }}>
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'rgba(255,255,255,.6)' }} />
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>Loading payment methods...</span>
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {paymentMethods.map((method) => (
                      <div key={method.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 13, background: 'rgba(255,255,255,.04)', border: method.isDefault ? '1px solid rgba(40,192,208,.25)' : '1px solid rgba(255,255,255,.08)' }}>
                        {getCardBrandDisplay(method.brand)}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', fontFamily: 'var(--sz-font-mono)' }}>
                            {method.brand} •••• {method.last4}
                          </div>
                          <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                            Expires {method.exp_month}/{method.exp_year}
                          </div>
                        </div>
                        {method.isDefault ? (
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#28C0D0', background: 'rgba(40,192,208,.16)', padding: '3px 10px', borderRadius: 999, flex: '0 0 auto' }}>Default</span>
                        ) : (
                          <button type="button" onClick={() => handleSetDefaultCard(method.id)} style={{ fontSize: 12, fontWeight: 600, color: '#28C0D0', background: 'none', border: 'none', cursor: 'pointer', flex: '0 0 auto' }}>
                            Set default
                          </button>
                        )}
                        <button type="button" onClick={() => handleRemovePaymentMethod(method.id)} style={{ ...removeLinkStyle, flex: '0 0 auto' }}>
                          Remove
                        </button>
                      </div>
                    ))}
                    <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,.4)', textAlign: 'center', marginTop: 4 }}>
                      Payment methods are securely stored with Stripe
                    </p>
                  </div>
                ) : (
                  <div style={{ padding: '34px 20px', borderRadius: 13, border: '1px dashed rgba(255,255,255,.18)', textAlign: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.05)' }}>
                      <CreditCard className="w-7 h-7" style={{ color: 'rgba(255,255,255,.5)' }} />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>No payment method added</div>
                    <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)', marginBottom: 16 }}>Add a card to hire crew and pay for leads.</div>
                    <button
                      type="button"
                      onClick={handleAddPaymentMethod}
                      data-testid="add-payment-button"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 20px', borderRadius: 11, background: '#28C0D0', border: 'none', color: '#0C1120', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                    >
                      <CreditCard className="w-4 h-4" />
                      Add payment method
                    </button>
                  </div>
                )}
              </div>

              {/* Security · Passkeys */}
              <div ref={el => sectionRefs.current.security = el} id="security" style={cardStyle}>
                {passkeySupported ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={cardTitleStyle}>Security · Passkeys</div>
                      <button
                        type="button"
                        onClick={handleRegisterPasskey}
                        disabled={registeringPasskey}
                        style={{ ...addBtnStyle, opacity: registeringPasskey ? 0.6 : 1 }}
                      >
                        {registeringPasskey ? (
                          <>
                            <Loader2 className="w-[15px] h-[15px] animate-spin" />
                            Registering...
                          </>
                        ) : (
                          <>
                            <Plus className="w-[15px] h-[15px]" strokeWidth={2.4} />
                            {passkeys.length > 0 ? 'Add another passkey' : 'Add passkey'}
                          </>
                        )}
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(103,198,180,.07)', border: '1px solid rgba(103,198,180,.2)' }}>
                      <Fingerprint className="w-5 h-5 shrink-0" style={{ marginTop: 1, color: '#67C6B4' }} />
                      <div>
                        <p style={{ fontSize: 12.5, fontWeight: 600, color: '#fff', margin: 0 }}>What are passkeys?</p>
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginTop: 4, marginBottom: 0, lineHeight: 1.5 }}>
                          A secure, phishing-resistant alternative to passwords. Use your device's Face ID, Touch ID or PIN to sign in instantly.
                        </p>
                      </div>
                    </div>

                    {loadingPasskeys ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '34px 0' }}>
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'rgba(255,255,255,.6)' }} />
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>Loading passkeys...</span>
                      </div>
                    ) : passkeys.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {passkeys.map((passkey) => (
                          <div key={passkey.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 13, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)' }}>
                            <span style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(103,198,180,.16)', color: '#67C6B4', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                              <Smartphone className="w-[18px] h-[18px]" />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{passkey.device_name}</div>
                              <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.5)' }}>
                                Added {formatDate(passkey.created_at)} · Last used {formatDate(passkey.last_used_at)}
                              </div>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button type="button" style={{ ...removeLinkStyle, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <Trash2 className="w-3 h-3" />
                                  Remove
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent style={dialogStyle}>
                                <AlertDialogHeader>
                                  <AlertDialogTitle style={{ color: '#fff' }}>Remove this passkey?</AlertDialogTitle>
                                  <AlertDialogDescription style={{ color: 'rgba(255,255,255,.6)' }}>
                                    You won't be able to use this passkey to sign in anymore. You can always add a new one later.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}>
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeletePasskey(passkey.id)}
                                    style={{ background: 'rgba(185,28,28,.18)', border: '1px solid rgba(185,28,28,.45)', color: '#F87171' }}
                                  >
                                    Remove passkey
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '34px 20px', borderRadius: 13, border: '1px dashed rgba(255,255,255,.18)', textAlign: 'center' }}>
                        <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.05)' }}>
                          <Fingerprint className="w-7 h-7" style={{ color: 'rgba(255,255,255,.5)' }} />
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>No passkeys registered yet</div>
                        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.5)' }}>Add a passkey to sign in faster and more securely.</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ ...cardTitleStyle, marginBottom: 16 }}>Security</div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px', borderRadius: 12, background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.22)' }}>
                      <AlertTriangle className="w-5 h-5 shrink-0" style={{ marginTop: 1, color: '#FBBF24' }} />
                      <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,.65)', margin: 0, lineHeight: 1.5 }}>
                        Your browser doesn't support passkeys. Try a modern browser like Chrome, Safari, or Edge to set up biometric sign-in.
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Account */}
              <div ref={el => sectionRefs.current.account = el} id="account" style={{ ...cardStyle, background: 'rgba(185,28,28,.05)', border: '1px solid rgba(185,28,28,.25)' }}>
                <div style={{ ...cardTitleStyle, marginBottom: 6 }}>Account</div>
                {userData.email && (
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginBottom: 10 }}>
                    Signed in as <span style={{ color: 'rgba(255,255,255,.7)', fontWeight: 600 }}>{userData.email}</span>
                  </div>
                )}
                <div className="sz-grid-2c" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.55)', maxWidth: 520 }}>
                    Deleting your account is permanent — your profile, applications, messages and job history will be removed. Outstanding payments must be settled first.
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        type="button"
                        data-testid="delete-account-button"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '11px 18px', borderRadius: 11, background: 'transparent', border: '1px solid rgba(185,28,28,.5)', color: '#F87171', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', flex: '0 0 auto' }}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete my account
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent style={{ ...dialogStyle, border: '1px solid rgba(185,28,28,.35)' }}>
                      <AlertDialogHeader>
                        <AlertDialogTitle style={{ color: '#fff' }}>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription style={{ color: 'rgba(255,255,255,.6)' }}>
                          This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}>
                          Keep account
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          data-testid="confirm-delete-button"
                          style={{ background: 'rgba(185,28,28,.18)', border: '1px solid rgba(185,28,28,.45)', color: '#F87171' }}
                        >
                          Yes, delete my account
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Add Card Modal */}
      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={handleCardAdded}
        isDark={isDark}
      />
    </div>
  );
};

export default Settings;
